import React, { useState } from 'react';
import { ChevronDownIcon, EyeIcon, FileTextIcon } from 'lucide-react';
import type { DisclosureDocument } from '../types/disclosures';
import { pluralize } from '../utils/format';
interface DocumentListProps {
  documents: DisclosureDocument[];
  selectedIds: string[];
  onToggle: (documentId: string) => void;
  onPreview: (document: DisclosureDocument) => void;
  readOnly?: boolean;
}
const categoryTone: Record<string, string> = {
  Federal: 'bg-blue-50 text-blue-700',
  State: 'bg-violet-50 text-violet-700',
  Company: 'bg-slate-100 text-slate-600',
  Investor: 'bg-teal-50 text-teal-700'
};
export function DocumentList({
  documents,
  selectedIds,
  onToggle,
  onPreview,
  readOnly = false
}: DocumentListProps) {
  const [showExcluded, setShowExcluded] = useState(false);
  const included = documents.filter(doc => !doc.excluded);
  const excluded = documents.filter(doc => doc.excluded);
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-id="element-61" data-mp="37gn006y6omzk">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3" data-id="element-62" data-mp="1hf9pcg04e5nh">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-63" data-mp="1ci9hbzjpmstq">
          Document
        </h3>
        <p className="text-[12px] text-slate-400" data-id="element-64" data-mp="17gk2s9xa3x0w">
          {pluralize(selectedIds.length, 'document')} included
        </p>
      </div>

      <ul className="divide-y divide-slate-100" data-id="element-65" data-mp="yhgjb4u79flv">
        {included.map(doc => {
        const checked = selectedIds.includes(doc.id);
        return <li key={doc.id} className="group flex items-center gap-3 px-5 py-3" data-id="element-66" data-mp="tp5m49qug7x0">
              {!readOnly && <input type="checkbox" checked={checked} disabled={doc.required} onChange={() => onToggle(doc.id)} aria-label={`Include ${doc.name}`} className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20 disabled:opacity-50" data-id="element-67" data-mp="opj7bvvdajm3" />}
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-400" data-id="element-68" data-mp="8vcpezglmx9">
                <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-69" data-mp="3nbgm2cshzpzc" />
              </span>
              <button type="button" onClick={() => onPreview(doc)} className="min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-70" data-mp="3sajpjsstxo57">
                <p className="truncate text-[13.5px] font-medium text-slate-900 group-hover:text-blue-800" data-id="element-71" data-mp="nwlbils60w3i">
                  {doc.name}
                </p>
                <p className="mt-0.5 flex items-center gap-2 text-[12px] text-slate-500" data-id="element-72" data-mp="a33yzkxwcjmz">
                  <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${categoryTone[doc.category]}`} data-id="element-73" data-mp="f1x70xuezifr">
                    {doc.category}
                  </span>
                  <span data-id="element-74" data-mp="1gz4iq0lo0m8a">{pluralize(doc.pages, 'page')}</span>
                  {doc.required && <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400" data-id="element-75" data-mp="1cxiw3njbwtkw">
                      Required
                    </span>}
                </p>
              </button>
              <button type="button" onClick={() => onPreview(doc)} className="shrink-0 rounded-md p-1.5 text-slate-400 opacity-0 transition hover:bg-slate-50 hover:text-slate-700 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 group-hover:opacity-100" aria-label={`Preview ${doc.name}`} data-id="element-76" data-mp="1lpcjntx9z4jz">
                <EyeIcon className="h-4 w-4" data-id="element-77" data-mp="12z3h2ixktarr" />
              </button>
            </li>;
      })}
      </ul>

      {excluded.length > 0 && <div className="border-t border-slate-100 bg-slate-50/60" data-id="element-78" data-mp="2jfpm7mvub3u2">
          <button type="button" onClick={() => setShowExcluded(value => !value)} aria-expanded={showExcluded} className="flex w-full items-center justify-between px-5 py-3 text-[12.5px] font-medium text-slate-500 transition hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-79" data-mp="2edqg458ptstf">
            Excluded Documents ({excluded.length})
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showExcluded ? 'rotate-180' : ''}`} data-id="element-80" data-mp="11v313wychyue" />
          </button>
          {showExcluded && <div className="px-5 pb-4" data-id="element-81" data-mp="1btslpxhmlxec">
              <p className="text-[12.5px] text-slate-500" data-id="element-82" data-mp="1gm3od83r3xal">
                These documents were removed during generation and will not be delivered.
              </p>
              <ul className="mt-2 space-y-1.5" data-id="element-83" data-mp="1lkdev5ej69pv">
                {excluded.map(doc => <li key={doc.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2" data-id="element-84" data-mp="1pol89fvxoduf">
                    <span className="truncate text-[13px] text-slate-500 line-through" data-id="element-85" data-mp="3f5703fvzm8cr">
                      {doc.name}
                    </span>
                    <span className="shrink-0 text-[11.5px] text-slate-400" data-id="element-86" data-mp="362pcax0op7nn">
                      {doc.excludedReason}
                    </span>
                  </li>)}
              </ul>
            </div>}
        </div>}
    </section>;
}